/**
 * Offline-safe draft storage for the PMC Cleaning checklist.
 *
 * Why this exists
 * ----------------
 * Field supervisors often work in areas with patchy mobile coverage. They
 * can fill the Before-cleaning form, walk to the next asset, and then lose
 * network entirely while attempting to submit. Without local persistence,
 * the unsaved photos / remarks / answers are lost when the browser tab is
 * killed, the page times out, or the supervisor logs out and back in.
 *
 * This module persists the in-flight draft to `localStorage` (one key per
 * supervisor + park + date + kind) so:
 *   • A page reload, app crash, or logout/login restores the exact state.
 *   • Photos (base64) stay attached as long as quota allows.
 *   • Only on a successful submit do we wipe the draft.
 *
 * Storage budget
 * --------------
 * `localStorage` is typically 5-10 MB per origin. The cleaning photos go
 * through `stampGallery()` which scales / compresses them well below
 * 500 KB each. With ≤ 5 before-photos and ≤ 4 after-photos for the most
 * common multi-asset case, we stay safely under 5 MB. If a write fails
 * with QuotaExceededError, we strip the photos array and retry — the
 * other fields (asset selections, answers, remarks) are tiny and always
 * survive.
 */

const PREFIX = "pmc.cleaning.draft";

export function draftKey({ supervisorId, parkId, date, kind }) {
  return `${PREFIX}.${supervisorId || "_"}.${parkId || "_"}.${date || "_"}.${kind}`;
}

export function saveDraft(key, data) {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    // Quota likely exceeded — retry without photo blobs.
    try {
      const slim = stripPhotos(data);
      localStorage.setItem(key, JSON.stringify(slim));
      // eslint-disable-next-line no-console
      console.warn("[cleaningDraft] photos stripped due to storage quota", err);
    } catch (err2) {
      // eslint-disable-next-line no-console
      console.warn("[cleaningDraft] save failed", err2);
    }
  }
}

export function loadDraft(key) {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearDraft(key) {
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function stripPhotos(data) {
  if (!data || typeof data !== "object") return data;
  const out = { ...data };
  if (Array.isArray(out.beforePhotos)) out.beforePhotos = [];
  if (Array.isArray(out.afterPhotos)) out.afterPhotos = [];
  return out;
}
